import React, { useState } from 'react';
import { TIMELINE_CONFIG } from './constants';

const EventCard = ({ cardWidth, event, isActive, selectedMaxWidth, studyStatus, modification }) => {
  const renderField = (originalValue, fieldName) => {
    if (!modification || !(fieldName in modification)) {
      return originalValue;
    }

    const modValue = modification[fieldName];
    const isModified = modValue === null || (Array.isArray(modValue) && modValue.every(v => v === null));

    if (isModified) {
      if (studyStatus === 'learn') {
        return (
          <span className="bg-yellow-100 px-1 rounded">
            {originalValue}
          </span>
        );
      } else if (studyStatus === 'review') {
        return (
          <span className="bg-gray-300 text-transparent select-none px-1 rounded">
            {originalValue}
          </span>
        );
      }
    }

    return originalValue;
  };

  return (
    <div
      className={`p-2 rounded-t border border-gray-400 shadow-sm hover:shadow-md mb-0 hover:cursor-pointer transition-all duration-300 ease-in-out ${isActive ? 'bg-white' : 'bg-neutral-200'}`}
      style={{
        width: 'fit-content',
        minWidth: `${cardWidth}px`,
        ...(selectedMaxWidth ? { maxWidth: selectedMaxWidth } : {})
      }}
    >
      <h3
        className={`font-bold text-xs leading-tight mb-1 whitespace-nowrap overflow-hidden text-ellipsis transition-colors ${isActive ? 'text-gray-800' : 'text-gray-400'}`}>
        {renderField(event.name, 'name')}
      </h3>
      <p
        className={`text-[10px] leading-3 whitespace-nowrap overflow-hidden text-ellipsis transition-colors ${isActive ? 'text-gray-600' : 'text-stone-400'}`}>{event.shortDescription}</p>
    </div>
  );
};

const EventLeg = ({ height, date, zIndex, isActive, labelOffset, hideDate, studyStatus, modification, dateIndex }) => {
  const renderField = (originalValue, fieldName, index) => {
    if (!modification || !(fieldName in modification)) {
      return originalValue;
    }

    const modValue = modification[fieldName];
    let isModified = false;

    if (Array.isArray(modValue) && index !== undefined) {
      isModified = modValue[index] === null;
    } else {
      isModified = modValue === null || (Array.isArray(modValue) && modValue.every(v => v === null));
    }

    if (isModified) {
      if (studyStatus === 'learn') {
        return (
          <span className="bg-yellow-100 px-1 rounded">
            {originalValue}
          </span>
        );
      } else if (studyStatus === 'review') {
        return (
          <span className="bg-gray-300 text-transparent select-none px-1 rounded">
            {originalValue}
          </span>
        );
      }
    }

    return originalValue;
  };

  return (
    <div className="w-px relative" style={{ zIndex: isActive ? 10000 : zIndex }}>
      <div
        className={`w-px transition-all duration-300 ease-in-out ${isActive ? 'bg-gray-500' : 'bg-gray-300'}`}
        style={{
          height: `${height}px`
        }}
      />
      <div className="relative flex flex-col items-start -ml-[4px]">
        <div
          className={`w-[9px] h-[9px] rounded-full border border-gray-200 transition-colors ${isActive ? 'bg-gray-700' : 'bg-neutral-400'}`}
        />
        <span
          className={`absolute text-[10px] font-bold transition-colors w-max left-1/2 ${
            isActive ? 'text-gray-700' : (hideDate ? 'text-transparent' : 'text-neutral-400')
          }`}
          style={{ bottom: `-${TIMELINE_CONFIG.DATE_LABEL_BASE_OFFSET + (labelOffset || 0)}px` }}
        >
          {renderField(date, 'dates', dateIndex)}
        </span>
      </div>
    </div>
  );
};

const TimelineItem = ({ event, onClick, isSelected }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isActive = isHovered || isSelected;

  const studyStatus = event.studyStatus;
  const modification = event.modification;

  const isRangeEvent = event.timelinePoints && event.timelinePoints.length > 1;
  const startDateStr = event.dates ? event.dates[0] : '';
  const endDateStr = isRangeEvent && event.dates ? event.dates[event.dates.length - 1] : '';

  const cardWidth = event.adjustedCardWidth + 1;

  const endLegOffset = event.timeBasedWidth || 0;

  const verticalOffset = TIMELINE_CONFIG.TIMELINE_OFFSET;
  const legHeight = TIMELINE_CONFIG.BASE_LEG_HEIGHT + (event.track || 0) * TIMELINE_CONFIG.TRACK_HEIGHT;

  const cardZIndex = isHovered ? 10000 : (isSelected ? 9999 : 100);

  let selectedMaxWidth = undefined;
  if (isSelected && !isHovered && event.distanceToNextOnTrack !== undefined) {
    selectedMaxWidth = `${Math.max(event.distanceToNextOnTrack + 20, 40)}px`;
  }

  return (
    <div
      className="flex-shrink-0 flex flex-col justify-end items-start absolute min-w-min transition-all duration-300 ease-in-out"
      style={{ 
        left: `${event.leftPosition}px`, 
        bottom: `${verticalOffset}px`
      }}
    >
      <div 
        className="relative w-full" 
        style={{ zIndex: cardZIndex }} 
        onMouseEnter={() => setIsHovered(true)} 
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onClick(event)}
      >
        <EventCard 
          cardWidth={cardWidth} 
          event={event} 
          isActive={isActive} 
          selectedMaxWidth={selectedMaxWidth}
          studyStatus={studyStatus}
          modification={modification}
        />
      </div>
      <div className="w-full flex" style={{ position: 'relative' }}>
        <EventLeg 
          height={legHeight} 
          date={startDateStr} 
          zIndex={100} 
          isActive={isActive} 
          labelOffset={event.startLabelOffset} 
          hideDate={event.hideStartDate}
          studyStatus={studyStatus}
          modification={modification}
          dateIndex={0}
        />
        {isRangeEvent && (
          <div className="transition-all duration-300 ease-in-out" style={{ position: 'absolute', left: `${endLegOffset}px` }}>
            <EventLeg 
              height={legHeight} 
              date={endDateStr} 
              zIndex={100} 
              isActive={isActive} 
              labelOffset={event.endLabelOffset} 
              hideDate={event.hideEndDate}
              studyStatus={studyStatus}
              modification={modification}
              dateIndex={event.dates.length - 1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineItem;